'use client';
import {create} from "zustand";
import { immer } from 'zustand/middleware/immer'
import {IComparatorForm} from "@/@types/health/ComparatorForm";
import {getCookie, setCookie} from "@/app/utils/client-cookies";
import {HealthLandingStoreState} from "@/app/stores/health/health-landing-store";
import {keysToCamel} from "@/app/utils/health/health-comparator-utils";
import {IContractor} from "@/@types/health/Contractor";
import {IProspectInfo} from "@/@types/health/ProspectInfo";

export interface HealthComparatorStoreState {
    originRoute: string;
    currentStep: number;
    comparatorForm: IComparatorForm;
    setProperty: Function,
    setComparatorForm: Function,
    normalizedForm: Function,
}

const setupOriginRoute = () => {
    if (!getCookie('health_origin_route')) {
        setCookie('health_origin_route', 'devis-mutuelle-sante')

        return 'devis-mutuelle-sante'
    }

    return getCookie('health_origin_route')
}

export const useHealthComparatorStore = create<HealthComparatorStoreState>()(
    immer((set, get) => {
        return {
            originRoute: setupOriginRoute(),
            currentStep: 1,
            comparatorForm: {
                family_type: null,
                effective_date: new Date(),
                ab_test_landing_version: null,
                status: null,
                contractors: [
                    {
                        prospect_info: {
                            birthday_date: null,
                            address_zip_code: null,
                            phone: null,
                            prospect: {
                                email: null,
                            },
                            birth_country: null,
                            birth_city: null,
                            birth_department: null,
                            nationality: null,
                            address_street: null,
                            address_city: null,
                            gender: null,
                            first_name: null,
                            last_name: null,
                            birth_last_name: null,
                            address_extra_infos: null,
                            document: {
                                passport: null,
                                idCard: null,
                                documentGivenDate: null,
                                documentExpirationDate: null,
                                authority: null,
                                releaseDepartment: null,
                            },
                        },
                        social_regime: 0,
                        agricultural_status: null,
                        professional_activity: '',
                        jobChoice: null,
                        isRegimeAlsaceMoselle: false,
                        type: 'main',
                        social_number: null,
                        is_retired: null,
                        family_status: null,
                        twin_birth_order: null,
                        social_security_provider: null,
                    },
                ],
                offer_id: null,
                offer_cost: null,
                offer_company_name: null,
                offer_name: null,
                rm_product_name: null,
                already_has_contract: null,
                insured_more_than_12_months: null,
                ask_previous_contract_infos: false,
                current_insurer: null,
                current_insurance_contract_number: null,
                bankHolder: null,
                bankIBAN: null,
                bankBIC: null,
                debit_day: null,
                exactData: false,
                cguRead: false,
                insurance_terms: '',
                selected_options: null,
                cloud_files: [],
                sponsorCode: null,
                token: '',
                bank_holder: null,
            },

            setProperty: (propertyPath: string, value: any) =>
                set((state) => {
                    const path = propertyPath.split('.');
                    let obj: any = state.comparatorForm; // Directly modify the draft

                    for (let i = 0; i < path.length - 1; i++) {
                        const key = path[i];

                        if (!obj[key] || typeof obj[key] !== 'object') {
                            obj[key] = {}; // Ensure intermediate objects exist
                        }

                        obj = obj[key];
                    }

                    obj[path[path.length - 1]] = value; // Mutate the draft directly
                }),

            setComparatorForm: (form: any) => set((state) => {
                state.comparatorForm = form
            }),

            normalizedForm: (form: IComparatorForm) => {
                const normalizedForm = JSON.parse(JSON.stringify(form))
                const contractors = normalizedForm.contractors.slice()
                const formattedContractors = []
                const mainContractor = normalizedForm.contractors[0]
                mainContractor.is_retired = !!mainContractor.is_retired
                mainContractor.prospectInfo = keysToCamel({...mainContractor.prospect_info})
                formattedContractors.push(mainContractor)

                let partnerContractor = contractors.filter((contractor: IContractor) => contractor.type === 'partner')
                const childrenContractors = contractors.filter((contractor: IContractor) => contractor.type === 'children')

                const mainProspectInfo = normalizedForm.contractors[0].prospect_info

                if (partnerContractor.length) {
                    partnerContractor = partnerContractor[0]
                    const partner = JSON.parse(JSON.stringify(partnerContractor))
                    partner.prospect_info.address_zip_code = mainProspectInfo.address_zip_code
                    partner.prospectInfo = keysToCamel({...partner.prospect_info})
                    formattedContractors.push(partner)
                }

                if (childrenContractors.length > 0) {
                    childrenContractors.forEach((child: IContractor) => {
                        const elem = JSON.parse(JSON.stringify(child))
                        elem.prospect_info.address_zip_code = mainProspectInfo.address_zip_code
                        elem.prospectInfo = keysToCamel({...elem.prospect_info})
                        elem.type = 'children'
                        formattedContractors.push(elem)
                    })
                }

                normalizedForm.contractors = formattedContractors
                normalizedForm.token = form.token

                normalizedForm.bankHolder = normalizedForm.bank_holder

                return normalizedForm
            },
        } as HealthComparatorStoreState
    })
)
